import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadDoneIcon from "@mui/icons-material/DownloadDone";
import FileDownloadDoneIcon from "@mui/icons-material/FileDownloadDone";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import i18n from "src/i18n";
import {
  useImportRetmMenuEntities,
  useRetmMenuEntities,
} from "src/api/integrations/retm";
import type {
  RetmMenuCategoryEntity,
  RetmMenuProductEntity,
} from "src/types/retm";
import { notificationsState } from "src/store/notifications";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";

type ImportType = "categories" | "products" | "both";

const RetmMenuImport = () => {
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [importType, setImportType] = useState<ImportType>("both");
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [productsPage, setProductsPage] = useState(0);
  const [productsPerPage, setProductsPerPage] = useState(25);
  const [categoriesPage, setCategoriesPage] = useState(0);
  const [categoriesPerPage, setCategoriesPerPage] = useState(100);

  const { data, isLoading, refetch } = useRetmMenuEntities({
    products_page: productsPage + 1,
    products_per_page: productsPerPage,
    categories_page: categoriesPage + 1,
    categories_per_page: categoriesPerPage,
  });
  const { mutate: importItems, status: importStatus } = useImportRetmMenuEntities();

  const currentLang = i18n.language || "en";
  const categories = data?.data?.categories ?? [];
  const products = data?.data?.products ?? [];
  const productsPagination = data?.data?.products_pagination;
  const categoriesPagination = data?.data?.categories_pagination;

  useEffect(() => {
    setSelectedCategoryIds([]);
    setSelectedProductIds([]);
  }, [importType]);

  useEffect(() => {
    if (importStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("menuImport.importSuccess"),
        },
      ]);
      refetch();
      setSelectedCategoryIds([]);
      setSelectedProductIds([]);
    } else if (importStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("menuImport.importError"),
        },
      ]);
    }
  }, [importStatus]);

  const getLocalized = (text?: { en?: string; ar?: string }): string =>
    text?.[currentLang as "en" | "ar"] || text?.en || text?.ar || "-";

  const toggleCategory = (id: string) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleProduct = (id: string) => {
    setSelectedProductIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const canImport =
    (importType === "categories" && selectedCategoryIds.length > 0) ||
    (importType === "products" && selectedProductIds.length > 0) ||
    (importType === "both" &&
      (selectedCategoryIds.length > 0 || selectedProductIds.length > 0));

  const allVisibleCategoriesSelected =
    categories.filter((c) => !c.already_imported).length > 0 &&
    categories
      .filter((c) => !c.already_imported)
      .every((category) =>
      selectedCategoryIds.includes(category.external_id)
    );

  const allVisibleProductsSelected =
    products.filter((p) => !p.already_imported).length > 0 &&
    products
      .filter((p) => !p.already_imported)
      .every((product) =>
      selectedProductIds.includes(product.external_id)
    );

  const toggleSelectAllCategories = () => {
    if (allVisibleCategoriesSelected) {
      setSelectedCategoryIds([]);
      return;
    }
    setSelectedCategoryIds(
      categories
        .filter((category) => !category.already_imported)
        .map((category) => category.external_id)
    );
  };

  const toggleSelectAllProducts = () => {
    if (allVisibleProductsSelected) {
      setSelectedProductIds([]);
      return;
    }
    setSelectedProductIds(
      products
        .filter((product) => !product.already_imported)
        .map((product) => product.external_id)
    );
  };

  const onImport = () => {
    const categoriesPayload =
      importType === "categories" || importType === "both"
        ? visibleCategories
            .filter((c) => !c.already_imported && selectedCategoryIds.includes(c.external_id))
            .map((c: RetmMenuCategoryEntity) => ({
              id: c.external_id,
              name: c.name,
            }))
        : [];

    const productsPayload =
      importType === "products" || importType === "both"
        ? visibleProducts
            .filter((p) => !p.already_imported && selectedProductIds.includes(p.external_id))
            .map((p: RetmMenuProductEntity) => ({
              id: p.external_id,
              name: p.name,
              price: p.price ?? null,
              type: p.type ?? null,
              category: p.category ?? null,
            }))
        : [];

    importItems({
      categories: categoriesPayload,
      products: productsPayload,
    });
  };

  const importSingleCategory = (category: RetmMenuCategoryEntity) => {
    importItems({
      categories: [{ id: category.external_id, name: category.name }],
      products: [],
    });
  };

  const importSingleProduct = (product: RetmMenuProductEntity) => {
    importItems({
      categories: [],
      products: [
        {
          id: product.external_id,
          name: product.name,
          price: product.price ?? null,
          type: product.type ?? null,
          category: product.category ?? null,
        },
      ],
    });
  };

  const visibleCategories = categories;
  const visibleProducts = products;

  return (
    <Stack gap={3} p={{ xs: 2, sm: "8px 32px 32px 32px" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/integrations/retm")}
            sx={{ color: "#667085" }}
          >
            {generalT("back")}
          </Button>
          <Box>
            <Typography fontSize={24} fontWeight={600} color="#101828">
              {t("menuImport.title")}
            </Typography>
            <Typography fontSize={14} color="#667085">
              {t("menuImport.subtitle")}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => refetch()}>
            {t("actions.refresh")}
          </Button>
          <Button
            variant="contained"
            startIcon={
              importStatus === "pending" ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DownloadDoneIcon />
              )
            }
            disabled={!canImport || importStatus === "pending"}
            onClick={onImport}
          >
            {t("menuImport.importItems")}
          </Button>
        </Stack>
      </Box>

      <Box bgcolor="#F2F4F7" width="100%" height="1px" />

      <FormControl size="small" sx={{ maxWidth: 260 }}>
        <InputLabel>{t("menuImport.typeLabel")}</InputLabel>
        <Select
          value={importType}
          label={t("menuImport.typeLabel")}
          onChange={(e) => setImportType(e.target.value as ImportType)}
        >
          <MenuItem value="both">{t("menuImport.types.both")}</MenuItem>
          <MenuItem value="categories">{t("menuImport.types.categories")}</MenuItem>
          <MenuItem value="products">{t("menuImport.types.products")}</MenuItem>
        </Select>
      </FormControl>

      {(importType === "categories" || importType === "both") && (
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Typography fontSize={16} fontWeight={600} color="#101828">
              {t("menuImport.categories")}
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={toggleSelectAllCategories}
              disabled={categories.filter((c) => !c.already_imported).length === 0}
            >
              {allVisibleCategoriesSelected
                ? t("menuImport.unselectAll")
                : t("menuImport.selectAll")}
            </Button>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("menuImport.table.select")}</TableCell>
                  <TableCell>{t("menuImport.table.externalId")}</TableCell>
                  <TableCell>{t("menuImport.table.name")}</TableCell>
                  <TableCell>{t("menuImport.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableLoadingSkeleton rowsLength={5} cellsLength={4} />
              ) : (
                <TableBody>
                  {visibleCategories.map((category) => (
                    <TableRow key={category.external_id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedCategoryIds.includes(category.external_id)}
                          onChange={() => toggleCategory(category.external_id)}
                          disabled={category.already_imported}
                        />
                      </TableCell>
                      <TableCell>{category.external_id}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography fontSize={14}>{getLocalized(category.name)}</Typography>
                          {category.already_imported && (
                            <Chip size="small" color="success" label={t("menuImport.imported")} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={category.already_imported || importStatus === "pending"}
                          onClick={() => importSingleCategory(category)}
                        >
                          <FileDownloadDoneIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            {!isLoading && categories.length === 0 && <NoData />}
          </TableContainer>
          <TablePagination
            component="div"
            labelRowsPerPage={generalT("rowsPerPage")}
            count={categoriesPagination?.total ?? visibleCategories.length}
            page={Math.max((categoriesPagination?.current ?? categoriesPage + 1) - 1, 0)}
            onPageChange={(_, newPage) => setCategoriesPage(newPage)}
            rowsPerPage={categoriesPagination?.per ?? categoriesPerPage}
            onRowsPerPageChange={(event) => {
              const value = parseInt(event.target.value, 10);
              setCategoriesPerPage(value);
              setCategoriesPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Box>
      )}

      {(importType === "products" || importType === "both") && (
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Typography fontSize={16} fontWeight={600} color="#101828">
              {t("menuImport.products")}
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={toggleSelectAllProducts}
              disabled={products.filter((p) => !p.already_imported).length === 0}
            >
              {allVisibleProductsSelected
                ? t("menuImport.unselectAll")
                : t("menuImport.selectAll")}
            </Button>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("menuImport.table.select")}</TableCell>
                  <TableCell>{t("menuImport.table.externalId")}</TableCell>
                  <TableCell>{t("menuImport.table.name")}</TableCell>
                  <TableCell>{t("menuImport.table.category")}</TableCell>
                  <TableCell>{t("menuImport.table.price")}</TableCell>
                  <TableCell>{t("menuImport.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableLoadingSkeleton rowsLength={5} cellsLength={6} />
              ) : (
                <TableBody>
                  {visibleProducts.map((product) => (
                    <TableRow key={product.external_id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedProductIds.includes(product.external_id)}
                          onChange={() => toggleProduct(product.external_id)}
                          disabled={product.already_imported}
                        />
                      </TableCell>
                      <TableCell>{product.external_id}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography fontSize={14}>{getLocalized(product.name)}</Typography>
                          {product.already_imported && (
                            <Chip size="small" color="success" label={t("menuImport.imported")} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{getLocalized(product.category?.title)}</TableCell>
                      <TableCell>{product.price ?? "-"}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={product.already_imported || importStatus === "pending"}
                          onClick={() => importSingleProduct(product)}
                        >
                          <FileDownloadDoneIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            {!isLoading && products.length === 0 && <NoData />}
          </TableContainer>
          <TablePagination
            component="div"
            labelRowsPerPage={generalT("rowsPerPage")}
            count={productsPagination?.total ?? visibleProducts.length}
            page={Math.max((productsPagination?.current ?? productsPage + 1) - 1, 0)}
            onPageChange={(_, newPage) => setProductsPage(newPage)}
            rowsPerPage={productsPagination?.per ?? productsPerPage}
            onRowsPerPageChange={(event) => {
              const value = parseInt(event.target.value, 10);
              setProductsPerPage(value);
              setProductsPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      )}
    </Stack>
  );
};

export default RetmMenuImport;
